/* Recent Photos motion settings.
 *
 * One source of truth for the organization-level values the settings modal
 * writes and the display renderer reads, so the editor and the screen can never
 * disagree about what a stored value means.
 */

import {
  DISPLAY_DURATION_DEFAULT_SECONDS,
  DISPLAY_DURATION_MAX_SECONDS,
  DISPLAY_DURATION_MIN_SECONDS,
  clampDisplayDurationSeconds,
} from '@/utils/display_duration';

export type RecentPhotoCarouselDirection = 'horizontal' | 'vertical';
export type RecentPhotoDisplayMode = 'carousel' | 'filmstrip';

// Organization tempo in seconds: the stationary pause after a stepped move,
// or the approximate travel time of one photo position in continuous mode.
//
// This is `organization.recentPhotosDurationSecs`, and the bounds are NOT
// redeclared here: they come from the display-duration engine the settings
// modal edits with, so a value the modal accepts (up to 10 minutes) can never
// be clamped to something shorter on its way to the screen.
export const RECENT_PHOTO_SPEED_MIN = DISPLAY_DURATION_MIN_SECONDS;
export const RECENT_PHOTO_SPEED_MAX = DISPLAY_DURATION_MAX_SECONDS;
export const RECENT_PHOTO_SPEED_DEFAULT = DISPLAY_DURATION_DEFAULT_SECONDS;

// Horizontal by default: a screen is wider than it is tall, so a sideways strip
// is what fills it.
export const RECENT_PHOTO_DIRECTION_DEFAULT: RecentPhotoCarouselDirection = 'horizontal';
export const RECENT_PHOTO_DISPLAY_MODE_DEFAULT: RecentPhotoDisplayMode = 'carousel';
export const RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_MIN = 1;
export const RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_MAX = 6;
export const RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_DEFAULT = 3;
export const RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_OPTIONS = [1, 2, 3, 4, 5, 6];
export const RECENT_PHOTO_IMAGE_SPACING_MIN = 0;
export const RECENT_PHOTO_IMAGE_SPACING_MAX = 80;
export const RECENT_PHOTO_IMAGE_SPACING_DEFAULT = 12;
export const RECENT_PHOTO_IMAGE_SPACING_OPTIONS = [0, 4, 8, 12, 20, 32, 48];

export const RECENT_PHOTO_DISPLAY_MODE_OPTIONS: Array<{
  value: RecentPhotoDisplayMode;
  label: string;
  hint: string;
}> = [
  {
    value: 'carousel',
    label: 'Stepped Filmstrip',
    hint: 'Moves the photo row one image at a time, pausing between each step.',
  },
  {
    value: 'filmstrip',
    label: 'Continuous Filmstrip',
    hint: 'Moves photos side by side in one smooth, uninterrupted stream.',
  },
];

export const RECENT_PHOTO_DIRECTION_OPTIONS: Array<{
  value: RecentPhotoCarouselDirection;
  label: string;
  hint: string;
  icon: string;
}> = [
  {
    value: 'horizontal',
    label: 'Horizontal',
    hint: 'Photos slide across, right to left.',
    icon: 'pi-arrow-left',
  },
  {
    value: 'vertical',
    label: 'Vertical',
    hint: 'Photos slide upward, one above the next.',
    icon: 'pi-arrow-up',
  },
];

// How long one stepped movement takes. Deliberately not configurable: the
// organization duration is the pause between movements, while this short
// transition keeps every step smooth and consistent.
export const RECENT_PHOTO_TRANSITION_MS = 700;

// The single clamp both surfaces use.
export const normalizeRecentPhotoSpeed = (value: unknown): number =>
  clampDisplayDurationSeconds(value);

export const normalizeRecentPhotoDirection = (
  value: unknown
): RecentPhotoCarouselDirection =>
  String(value ?? '').trim().toLowerCase() === 'vertical'
    ? 'vertical'
    : RECENT_PHOTO_DIRECTION_DEFAULT;

export const normalizeRecentPhotoDisplayMode = (
  value: unknown
): RecentPhotoDisplayMode =>
  String(value ?? '').trim().toLowerCase() === 'filmstrip'
    ? 'filmstrip'
    : RECENT_PHOTO_DISPLAY_MODE_DEFAULT;

export const normalizeRecentPhotoFilmstripVisibleCount = (value: unknown): number => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_DEFAULT;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_DEFAULT;

  return Math.min(
    RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_MAX,
    Math.max(RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_MIN, Math.round(parsed))
  );
};

export const normalizeRecentPhotoImageSpacing = (value: unknown): number => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return RECENT_PHOTO_IMAGE_SPACING_DEFAULT;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return RECENT_PHOTO_IMAGE_SPACING_DEFAULT;

  return Math.min(
    RECENT_PHOTO_IMAGE_SPACING_MAX,
    Math.max(RECENT_PHOTO_IMAGE_SPACING_MIN, Math.round(parsed))
  );
};

// The settings as the display reads them, from whatever the organization
// row holds (including nothing at all, on an organization that has never opened
// the settings modal).
export const readRecentPhotoCarouselSettings = (
  organization: Record<string, any> | null | undefined
): {
  speed: number;
  direction: RecentPhotoCarouselDirection;
  mode: RecentPhotoDisplayMode;
  visibleCount: number;
  spacing: number;
} => ({
  speed: normalizeRecentPhotoSpeed(organization?.recentPhotosDurationSecs),
  direction: normalizeRecentPhotoDirection(organization?.recentPhotosCarouselDirection),
  mode: normalizeRecentPhotoDisplayMode(organization?.recentPhotosDisplayMode),
  visibleCount: normalizeRecentPhotoFilmstripVisibleCount(
    organization?.recentPhotosFilmstripVisibleCount
  ),
  spacing: normalizeRecentPhotoImageSpacing(organization?.recentPhotosImageSpacingPx),
});
